/** Pure helpers for the R-01 billed-actuals worklist (filter / sort / roll-up).
 *  Kept out of the component so they're unit-testable, mirroring upsell_ops/lib. */

import type { DataTableSort } from "@/components/cinch/DataTable/DataTable";

import type { BilledAccount, ReviewStatus } from "./api";

/** Below a cent, the two sources are considered in agreement. */
export const MATCH_TOLERANCE = 0.01;

export type ChangeKind = "new" | "stopped" | "increased" | "decreased" | "flat";

export interface BilledFilters {
  q: string;
  review: ReviewStatus[];
  change: ChangeKind[];
  /** Only accounts where the waterfall and raw QB disagree. */
  mismatch: boolean;
  /** Only accounts with MRR this month. */
  mrr: boolean;
}

export function classifyChange(a: BilledAccount): ChangeKind {
  const prior = a.billed_prior;
  const cur = a.billed_current;
  if (Math.abs(prior) < MATCH_TOLERANCE && Math.abs(cur) >= MATCH_TOLERANCE) return "new";
  if (Math.abs(prior) >= MATCH_TOLERANCE && Math.abs(cur) < MATCH_TOLERANCE) return "stopped";
  if (cur > prior + 1) return "increased";
  if (cur < prior - 1) return "decreased";
  return "flat";
}

/** The waterfall and raw QB disagree for this account (beyond a cent).
 *  A missing QB total counts when the waterfall billed something, and a
 *  QB-only account (nothing in the waterfall) always mismatches. */
export function sourcesMismatch(a: BilledAccount): boolean {
  return Math.abs((a.qb_invoice_total ?? 0) - a.billed_current) > MATCH_TOLERANCE;
}

export function filterAccounts(
  accounts: BilledAccount[],
  f: BilledFilters,
): BilledAccount[] {
  const q = f.q.trim().toLowerCase();
  return accounts.filter((a) => {
    if (q) {
      const hay = `${a.qb_id} ${a.cinch_account_name ?? ""} ${a.qb_customer_name ?? ""}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    if (f.review.length && !f.review.includes(a.review_status)) return false;
    if (f.change.length && !f.change.includes(classifyChange(a))) return false;
    if (f.mismatch && !sourcesMismatch(a)) return false;
    if (f.mrr && Math.abs(a.mrr_current) < MATCH_TOLERANCE) return false;
    return true;
  });
}

function sortValue(a: BilledAccount, key: string): number | string | null {
  switch (key) {
    case "qb_id":
      return a.qb_id;
    case "cinch_account_name":
      return a.cinch_account_name?.toLowerCase() ?? null;
    case "qb_customer_name":
      return a.qb_customer_name?.toLowerCase() ?? null;
    case "billed_prior":
      return a.billed_prior;
    case "delta":
      return a.billed_current - a.billed_prior;
    case "billed_current":
      return a.billed_current;
    case "mrr_current":
      return a.mrr_current;
    case "qb_check":
      return (a.qb_invoice_total ?? 0) - a.billed_current;
    case "review":
      return a.review_status;
    default:
      return null;
  }
}

export function sortAccounts(
  accounts: BilledAccount[],
  sort: DataTableSort | null,
): BilledAccount[] {
  if (!sort) return accounts;
  const dir = sort.dir === "asc" ? 1 : -1;
  return [...accounts].sort((x, y) => {
    const vx = sortValue(x, sort.key);
    const vy = sortValue(y, sort.key);
    if (vx == null && vy == null) return 0;
    if (vx == null) return 1; // nulls last regardless of direction
    if (vy == null) return -1;
    if (vx < vy) return -1 * dir;
    if (vx > vy) return 1 * dir;
    return 0;
  });
}

export interface BilledTotals {
  count: number;
  prior: number;
  current: number;
  mrr: number;
  qb: number;
  qbDelta: number;
  mismatches: number;
  pending: number;
  approved: number;
  flagged: number;
}

/** Client-side roll-up (used for tiles + the footer over the FILTERED rows) so
 *  a review mutation patched into the cache keeps every count fresh. */
export function totalAccounts(accounts: BilledAccount[]): BilledTotals {
  const t: BilledTotals = {
    count: accounts.length,
    prior: 0, current: 0, mrr: 0, qb: 0, qbDelta: 0,
    mismatches: 0, pending: 0, approved: 0, flagged: 0,
  };
  for (const a of accounts) {
    t.prior += a.billed_prior;
    t.current += a.billed_current;
    t.mrr += a.mrr_current;
    t.qb += a.qb_invoice_total ?? 0;
    if (sourcesMismatch(a)) t.mismatches += 1;
    if (a.review_status === "pending") t.pending += 1;
    if (a.review_status === "approved") t.approved += 1;
    if (a.review_status === "flagged") t.flagged += 1;
  }
  t.qbDelta = t.qb - t.current;
  return t;
}
